import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaInfoCircle,
  FaCreditCard,
  FaMoneyBillWave,
  FaMobile,
  FaLock,
} from "react-icons/fa";
import {
  calculateCabPrice,
  calculateTaxesAndFees,
  calculateTotalAmount,
} from "../utils/priceCalculator";
import CouponApplicator from "../components/CouponApplicator";

const Payment = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // State declarations
  const [booking, setBooking] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPartialPayment, setIsPartialPayment] = useState(false);
  const [advancePercentage, setAdvancePercentage] = useState(20); // Default 20% advance
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [cabDetails, setCabDetails] = useState(null);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [originalTotal, setOriginalTotal] = useState(0);

  // Calculate total amount for display with proper coupon discount order
  const calculateTotalForDisplay = () => {
    const urlParams = new URLSearchParams(location.search);

    // Get distance from URL params, then booking state, or default to 0
    let distance = 0;
    const distanceFromUrl = urlParams.get("distance");

    if (distanceFromUrl && !isNaN(parseFloat(distanceFromUrl))) {
      distance = parseFloat(distanceFromUrl);
    } else if (booking?.distance) {
      distance = parseFloat(booking.distance);
    }

    // Ensure distance is a valid number and at least 1km
    distance = Math.max(1, distance || 0);

    const urlAmount =
      parseFloat(urlParams.get("amount")) || booking?.price || 0;

    // If URL amount is provided and valid, use it as the base amount (BEFORE taxes)
    if (urlAmount > 0) {
      // The URL amount should be treated as the base fare (before taxes)
      const baseFare = urlAmount;

      // Apply coupon discount to base fare FIRST if coupon is applied
      const discountedBaseFare = appliedCoupon
        ? Math.max(0, baseFare - appliedCoupon.discountAmount)
        : baseFare;

      // Calculate taxes on the discounted amount (18% GST of discounted base fare)
      const taxRate = 0.18;
      const taxes = Math.max(1, Math.round(discountedBaseFare * taxRate));

      // Final total is discounted base fare + taxes on discounted amount
      const total = discountedBaseFare + taxes;


      return {
        distance: Math.round(distance * 10) / 10, // Round to 1 decimal place
        baseFare, // Original base fare
        discountedBaseFare, // After coupon discount
        taxes, // Tax on discounted amount
        total, // Final total
        ratePerKm:
          urlAmount > 0 && distance > 0
            ? Math.round((baseFare / distance) * 10) / 10
            : 0,
      };
    } else {
      // Fallback to manual calculation if no valid URL amount
      // Get cab type from URL or use default rates
      const cabType = urlParams.get("cabType") || "standard";

      // Define rates based on cab type (in INR per km)
      const rates = {
        sedan: 12,
        innova: 16,
        suv: 18,
        luxury: 20,
        standard: 14, // Default rate
      };

      // Get rate based on cab type, fallback to standard if not found
      const ratePerKm = rates[cabType.toLowerCase()] || rates.standard;

      // Calculate base fare (distance * rate per km)
      const baseFareCalculated = Math.round(distance * ratePerKm);

      // Ensure minimum fare (at least 1.5x the base rate for the first 4km)
      const minimumFare = Math.round(ratePerKm * 4 * 1.5);
      const baseFare = Math.max(baseFareCalculated, minimumFare);

      // Apply coupon discount to base fare FIRST if coupon is applied
      const discountedBaseFare = appliedCoupon
        ? Math.max(0, baseFare - appliedCoupon.discountAmount)
        : baseFare;

      // Calculate taxes on the discounted amount (18% GST of discounted base fare)
      const taxes = Math.max(1, Math.round(discountedBaseFare * 0.18));

      // Final total is discounted base fare + taxes on discounted amount
      const total = discountedBaseFare + taxes;

      return {
        distance: Math.round(distance * 10) / 10, // Round to 1 decimal place
        baseFare, // Original base fare
        discountedBaseFare, // After coupon discount
        taxes, // Tax on discounted amount
        total, // Final total
        ratePerKm,
      };
    }
  };

  const {
    distance,
    baseFare,
    discountedBaseFare,
    taxes,
    total: calculatedTotal,
    ratePerKm,
  } = calculateTotalForDisplay();

  // Set original total (base fare without discount) and use calculated total
  useEffect(() => {
    setOriginalTotal(baseFare || calculatedTotal);
  }, [baseFare, calculatedTotal]);

  const total = calculatedTotal; // Total is already calculated with proper discount and tax order

  const calculatePaymentAmount = () => {
    return isPartialPayment
      ? Math.round((total * advancePercentage) / 100)
      : total;
  };

  // Handle coupon application
  const handleCouponApplied = (couponData) => {
    setAppliedCoupon(couponData);
    toast.success(
      `Coupon applied! You saved ₹${couponData.discountAmount?.toFixed(2)}`,
    );
    // Force recalculation by updating state
    setOriginalTotal(prev => prev);
  };

  // Handle coupon removal
  const handleCouponRemoved = () => {
    setAppliedCoupon(null);
    // Force recalculation by updating state
    setOriginalTotal(prev => prev);
  };

  // Get calculation breakdown for display
  const getCalculationBreakdown = () => {
    const baseAmount = baseFare || originalTotal;
    const discount = appliedCoupon?.discountAmount || 0;
    const afterDiscount = Math.max(0, baseAmount - discount);
    const taxAmount = taxes;
    const finalTotal = total;

    return {
      baseAmount,
      discount,
      afterDiscount,
      taxAmount,
      finalTotal
    };
  };

  const calculateRemainingAmount = () => {
    return total - calculatePaymentAmount();
  };

  const paymentAmount = calculatePaymentAmount();
  const remainingAmount = calculateRemainingAmount();

  // Handle payment processing
  const handlePayment = async () => {
    setIsProcessing(true);

    try {
      // Simulate payment processing delay
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Generate a booking ID (in a real app, this would come from backend)
      const bookingId = `CB${Math.floor(10000000 + Math.random() * 90000000)}`;

      // Prepare booking data to save (in a real app, save to backend)
      const bookingData = {
        id: bookingId,
        from: fromCity,
        to: toCity,
        cabType: booking?.cabType || "Standard",
        amount: total,
        originalAmount: originalTotal,
        discountAmount: appliedCoupon?.discountAmount || 0,
        appliedCoupon: appliedCoupon?.code || null,
        paymentMethod: paymentMethod === "online" ? "ONLINE" : "COD",
        isPartial: isPartialPayment,
        partialAmount: isPartialPayment ? paymentAmount : 0,
        status: paymentMethod === "online" ? "confirmed" : "pending",
        bookedAt: new Date().toISOString(),
        name,
        mobile,
        email,
        pickupAddress,
        dropAddress,
        travelDate,
        travelTime,
        distance: distanceParam,

        // Trip type information
        // Note: Backend model only supports oneWay and roundTrip, multiCity is handled via viaCities
        journeyType: queryParams.get("tripType") === "roundTrip" ? "roundTrip" : "oneWay",
        ...(queryParams.get("tripType") === "roundTrip" && {
          returnDate: queryParams.get("returnDate") || null,
          returnTime: queryParams.get("returnTime") || null,
        }),

        // Via cities for multi-city trips
        viaCities: queryParams.get("via")
          ? queryParams.get("via").split(",").map(city => city.trim()).filter(city => city.length > 0)
          : [],
      };

      // Save to localStorage (for demo purposes)
      localStorage.setItem(`booking_${bookingId}`, JSON.stringify(bookingData));

      if (paymentMethod === "online") {
        toast.success(
          isPartialPayment
            ? "Advance payment successful!"
            : "Payment successful! Booking confirmed.",
        );
      } else {
        toast.success("Booking confirmed! You will pay the driver on pickup.");
      }

      // Prepare parameters for booking confirmation page
      const confirmationParams = new URLSearchParams();
      confirmationParams.append("paymentStatus", "success");
      confirmationParams.append("from", fromCity);
      confirmationParams.append("to", toCity);
      confirmationParams.append("cabName", booking?.cabType || "Standard");
      confirmationParams.append("amount", total.toString());
      if (travelDate) confirmationParams.append("date", travelDate);
      if (travelTime) confirmationParams.append("time", travelTime);
      if (distanceParam > 0)
        confirmationParams.append("distance", distanceParam.toString());
      if (pickupAddress)
        confirmationParams.append("pickupAddress", pickupAddress);
      if (dropAddress) confirmationParams.append("dropAddress", dropAddress);
      if (name) confirmationParams.append("name", name);
      if (mobile) confirmationParams.append("mobile", mobile);
      if (email) confirmationParams.append("email", email);

      // Navigate to booking confirmation page
      navigate(`/booking/${bookingId}?${confirmationParams.toString()}`);
    } catch (error) {
      toast.error("Payment processing failed. Please try again.");
      console.error("Payment error:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Check authentication and fetch booking details
  useEffect(() => {
    const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
    const isLoggedIn = userInfo && userInfo.token;

    if (!isLoggedIn) {
      const currentPath = `${window.location.pathname}${window.location.search}`;
      toast.error("Please log in to continue with your booking");
      navigate(`/login?redirect=${encodeURIComponent(currentPath)}`);
      return;
    }

    setIsAuthenticated(true);

    // Set booking details from URL or state
    const params = new URLSearchParams(location.search);
    const distanceFromUrl = parseFloat(params.get("distance"));

    setBooking({
      from: params.get("from") || "N/A",
      to: params.get("to") || "N/A",
      date: params.get("date") || new Date().toLocaleDateString(),
      time: params.get("time") || "12:00 PM",
      cabType: params.get("cabType") || "Standard",
      price: parseFloat(params.get("amount")) || 0,
      distance: !isNaN(distanceFromUrl) ? distanceFromUrl : 0,
    });


    // Cleanup function
    return () => {
      // Any cleanup if needed
    };
  }, [location.search, navigate]);

  if (!isAuthenticated) {
    return null; // Or a loading spinner
  }

  // Extract query parameters
  const queryParams = new URLSearchParams(location.search);
  const fromCity = queryParams.get("from") || "";
  const toCity = queryParams.get("to") || "";
  const viaCitiesParam = queryParams.get("via");
  const viaCities = viaCitiesParam ? viaCitiesParam.split(',').map(city => city.trim()).filter(city => city) : [];
  const cabId = queryParams.get("cabId") || "";

  // Format route display with via cities
  const formatRoute = () => {
    if (viaCities.length > 0) {
      return `${fromCity} → ${viaCities.join(' → ')} → ${toCity}`;
    }
    return `${fromCity} → ${toCity}`;
  };
  const bookingType = queryParams.get("tripType") || queryParams.get("type") || "oneWay";
  const name = queryParams.get("name") || "";
  const mobile = queryParams.get("mobile") || "";
  const email = queryParams.get("email") || "";
  const pickupAddress = queryParams.get("pickupAddress") || "";
  const dropAddress = queryParams.get("dropAddress") || "";
  const travelDate = queryParams.get("date") || "";
  const travelTime = queryParams.get("time") || "";
  const distanceParam = parseFloat(queryParams.get("distance")) || 0;
  const amountParam = parseFloat(queryParams.get("amount")) || 0;

  return (
    <div className="container mx-auto py-8">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">Complete Your Payment</h1>

        {/* Trip Summary Section */}
        {fromCity && toCity && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Trip Summary</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Route:</span>
                <span className="font-medium">{formatRoute()}</span>
              </div>
              {travelDate && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Travel Date:</span>
                  <span className="font-medium">{new Date(travelDate).toLocaleDateString()}</span>
                </div>
              )}
              {travelTime && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Travel Time:</span>
                  <span className="font-medium">{travelTime}</span>
                </div>
              )}
              {distanceParam > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Distance:</span>
                  <span className="font-medium">{Math.round(distanceParam)} km</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="space-y-4">
                {/* Payment method selection */}
                <div className="mb-6">
                  <h2 className="text-lg font-semibold mb-4">
                    Select Payment Method
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setPaymentMethod("online")}
                      className={`p-4 border rounded-lg flex items-center justify-center space-x-2 ${paymentMethod === "online" ? "border-blue-500 bg-blue-50" : "border-gray-200"}`}
                    >
                      <FaCreditCard className="text-blue-500" />
                      <div className="text-center">
                        <div className="font-medium">Online Payment</div>
                        <div className="text-sm text-gray-600">
                          Pay now via Card/UPI
                        </div>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentMethod("COD")}
                      className={`p-4 border rounded-lg flex items-center justify-center space-x-2 ${paymentMethod === "COD" ? "border-green-500 bg-green-50" : "border-gray-200"}`}
                    >
                      <FaMoneyBillWave className="text-green-500" />
                      <div className="text-center">
                        <div className="font-medium">Cash on Delivery</div>
                        <div className="text-sm text-gray-600">
                          Pay to driver
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Online Payment Form */}
                {paymentMethod === "online" && (
                  <form onSubmit={(e) => handlePayment()} className="space-y-4">
                    <div>
                      <label className="block text-gray-700 mb-1">
                        Card Number
                      </label>
                      <input
                        type="text"
                        className="w-full border rounded-lg p-2"
                        placeholder="1234 5678 9012 3456"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-700 mb-1">
                          Expiry Date
                        </label>
                        <input
                          type="text"
                          className="w-full border rounded-lg p-2"
                          placeholder="MM/YY"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 mb-1">CVV</label>
                        <input
                          type="text"
                          className="w-full border rounded-lg p-2"
                          placeholder="123"
                          required
                        />
                      </div>
                    </div>

                    {/* Coupon Application */}
                    <CouponApplicator
                      bookingAmount={baseFare || originalTotal}
                      cabType={booking?.cabType || "standard"}
                      onCouponApplied={handleCouponApplied}
                      onCouponRemoved={handleCouponRemoved}
                    />

                    {/* Calculation Summary */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold text-blue-800 mb-3">Price Calculation</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Actual Price:</span>
                          <span>₹{(baseFare || originalTotal).toLocaleString("en-IN")}</span>
                        </div>
                        {appliedCoupon && (
                          <>
                            <div className="flex justify-between text-green-600">
                              <span>Coupon Discount ({appliedCoupon.code}):</span>
                              <span>-₹{appliedCoupon.discountAmount?.toLocaleString("en-IN") || "0"}</span>
                            </div>
                            <div className="flex justify-between border-t pt-2">
                              <span>Price After Discount:</span>
                              <span>₹{(discountedBaseFare || (baseFare || originalTotal)).toLocaleString("en-IN")}</span>
                            </div>
                          </>
                        )}
                        <div className="flex justify-between">
                          <span>GST (18%):</span>
                          <span>₹{taxes.toLocaleString("en-IN")}</span>
                        </div>
                        <div className="flex justify-between font-bold text-lg border-t pt-2 text-blue-600">
                          <span>Final Total:</span>
                          <span>₹{total.toLocaleString("en-IN")}</span>
                        </div>
                      </div>
                      <div className="mt-3 text-xs text-blue-700 bg-blue-100 p-2 rounded">
                        💡 <strong>How it works:</strong> Coupon discount is applied to the actual price first, then taxes are calculated on the discounted amount.
                      </div>
                    </div>

                    {/* Partial Payment Option */}
                    <div className="mb-6 bg-blue-50 p-4 rounded-lg border border-blue-100">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="partialPayment"
                            checked={isPartialPayment}
                            onChange={(e) =>
                              setIsPartialPayment(e.target.checked)
                            }
                            className="h-4 w-4 text-blue-600 rounded"
                          />
                          <label
                            htmlFor="partialPayment"
                            className="ml-2 text-sm font-medium text-gray-700"
                          >
                            Pay partial amount now
                          </label>
                        </div>
                        {isPartialPayment && (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-600">
                              Advance:
                            </span>
                            <select
                              value={advancePercentage}
                              onChange={(e) =>
                                setAdvancePercentage(Number(e.target.value))
                              }
                              className="border rounded-md p-1 text-sm"
                            >
                              <option value="20">20%</option>
                              <option value="30">30%</option>
                              <option value="50">50%</option>
                            </select>
                          </div>
                        )}
                      </div>
                      {isPartialPayment && (
                        <div className="text-sm text-gray-600">
                          <div className="flex justify-between mb-1">
                            <span>Pay now:</span>
                            <span className="font-semibold">
                              ₹{paymentAmount.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Pay to driver:</span>
                            <span className="font-semibold">
                              ₹{remainingAmount.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="submit"
                        className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-all flex items-center"
                        disabled={isProcessing}
                      >
                        {isProcessing ? (
                          <>
                            <svg
                              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Processing...
                          </>
                        ) : (
                          `Pay ₹${isPartialPayment ? paymentAmount.toFixed(2) : total.toFixed(2)} Now`
                        )}
                      </button>
                    </div>
                  </form>
                )}

                {/* COD Payment */}
                {paymentMethod === "COD" && (
                  <div className="space-y-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                      <h3 className="font-semibold text-amber-800 mb-2">
                        Cash on Delivery
                      </h3>
                      <p className="text-amber-700 text-sm">
                        You will pay the total amount of{" "}
                        <span className="font-semibold">
                          ₹{total.toFixed(2)}
                        </span>{" "}
                        directly to the driver at the time of pickup.
                      </p>
                      {appliedCoupon && (
                        <p className="text-green-700 text-xs mt-2">
                          ✅ Coupon discount has been applied to the actual price before adding taxes.
                        </p>
                      )}
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={handlePayment}
                        className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-all flex items-center"
                        disabled={isProcessing}
                      >
                        {isProcessing ? (
                          <>
                            <svg
                              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              />
                            </svg>
                            Processing...
                          </>
                        ) : isPartialPayment ? (
                          `Pay ₹${paymentAmount.toFixed(2)} Now`
                        ) : (
                          `Pay ₹${total.toFixed(2)}`
                        )}
                      </button>
                    </div>

                    <div className="border-t pt-4 mt-4">
                      <div className="flex justify-between mb-2">
                        <span className="text-gray-600">Cab Type</span>
                        <span className="font-medium">
                          {booking?.cabType || "N/A"}
                        </span>
                      </div>
                      {distanceParam > 0 && (
                        <div className="flex justify-between mb-2">
                          <span className="text-gray-600">Distance</span>
                          <span className="font-medium">
                            {Math.round(distanceParam).toLocaleString("en-IN")}{" "}
                            km
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="border-t pt-3 mt-3 space-y-2">
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Distance:</span>
                          <span>{distance.toFixed(1)} km</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Rate per km:</span>
                          <span>₹{ratePerKm}/km</span>
                        </div>
                        <div className="flex justify-between font-medium pt-1">
                          <span>Actual Price:</span>
                          <span>₹{(baseFare || originalTotal).toLocaleString("en-IN")}</span>
                        </div>
                        {appliedCoupon && (
                          <>
                            <div className="flex justify-between text-green-600">
                              <span>
                                Coupon Discount ({appliedCoupon?.code}):
                              </span>
                              <span>
                                -₹{appliedCoupon?.discountAmount?.toLocaleString("en-IN") || "0"}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Price After Discount:</span>
                              <span>
                                ₹{discountedBaseFare?.toLocaleString("en-IN") || (baseFare || originalTotal).toLocaleString("en-IN")}
                              </span>
                            </div>
                          </>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-600">GST (18%):</span>
                          <span>₹{taxes.toLocaleString("en-IN")}</span>
                        </div>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t mt-2 text-lg">
                        <span className="font-bold">Total Amount:</span>
                        <span className="font-bold text-blue-600">
                          ₹{total.toLocaleString("en-IN")}
                        </span>
                      </div>
                    </div>

                    {isPartialPayment && (
                      <div className="mt-3 pt-2 border-t">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            Pay now ({advancePercentage}%):
                          </span>
                          <span className="text-blue-600 font-medium">
                            ₹{paymentAmount.toLocaleString("en-IN")}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm mt-1">
                          <span className="text-gray-600">
                            Pay later ({100 - advancePercentage}%):
                          </span>
                          <span className="text-gray-600 font-medium">
                            ₹{remainingAmount.toLocaleString("en-IN")}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payment;
